// src/app/api/programmeApi.ts
import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type QuarterKey    = "Q1" | "Q2" | "Q3" | "Q4";
export type QuarterStatus = "completed" | "active" | "upcoming" | "locked";

// A ProgrammeCycle document — the admin-defined master record for a quarter.
export interface ProgrammeCycle {
  id:                        string | null;
  year?:                     number;
  quarter:                   QuarterKey;
  theme:                     string | null;
  tagline:                   string | null;
  status:                    QuarterStatus;
  pillars:                   string[];
  eventDate:                 string | null;
  taskSubmissionDeadline?:   string | null;
  fellowsSelectionDeadline?: string | null;
  shadowingWeekStart?:       string | null;
  shadowingWeekEnd?:         string | null;
  // Computed by the backend — no client date arithmetic needed
  daysToEvent:               number | null;
  progressPercent:           number;
  createdAt?:                string;
  updatedAt?:                string;
}

// Summary of one quarter relative to the mentee's personal position.
// Returned in the quarterSummary array of GET /my-quarter.
export interface QuarterSummaryItem {
  quarter:   QuarterKey;
  status:    QuarterStatus;   // personal status, not the global one
  theme:     string | null;
  tagline:   string | null;
  pillars:   string[];
  eventDate: string | null;
  cycleId:   string | null;
}

// Full response from GET /api/programme/my-quarter
export interface MyQuarterData {
  currentQuarter:    QuarterKey;
  nextQuarter:       QuarterKey | null;
  quartersCompleted: number;
  cycle:             ProgrammeCycle;      // the active quarter's full cycle
  quarterSummary:    QuarterSummaryItem[]; // all 4 quarters with personal status
}

interface CyclesResponse {
  success: boolean;
  message: string;
  data:    ProgrammeCycle[];
}

interface CycleResponse {
  success: boolean;
  message: string;
  data:    ProgrammeCycle;
}

interface MyQuarterResponse {
  success: boolean;
  message: string;
  data:    MyQuarterData;
}

interface AdvanceQuarterResponse {
  success: boolean;
  message: string;
  data: {
    previousQuarter:   QuarterKey;
    currentQuarter:    QuarterKey;
    quartersCompleted: number;
    nextCycle:         ProgrammeCycle | null;
  };
}

interface CreateCycleRequest {
  year:                      number;
  quarter:                   QuarterKey;
  theme:                     string;
  tagline?:                  string;
  status?:                   QuarterStatus;
  pillars?:                  string[];
  eventDate:                 string;
  taskSubmissionDeadline?:   string;
  fellowsSelectionDeadline?: string;
  needsAssessmentStart?:     string;
  needsAssessmentEnd?:       string;
  contentDevStart?:          string;
  contentDevEnd?:            string;
  mobilisationStart?:        string;
  mobilisationEnd?:          string;
  shadowingWeekStart?:       string;
  shadowingWeekEnd?:         string;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const programmeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // GET /api/programme/cycles — all quarters for the admin quarter selector
    getAllCycles: builder.query<CyclesResponse, void>({
      query:        () => "/programme/cycles",
      providesTags: [{ type: "ProgrammeCycle", id: "LIST" }],
    }),

    // GET /api/programme/my-quarter — the mentee's personal current quarter.
    //
    // THIS is the primary call made by the Home tab and all content tabs.
    // It replaces the old useGetActiveCycleQuery. Every new mentee starts
    // at Q1. The response includes:
    //   - cycle:          full detail of the mentee's current quarter
    //   - quarterSummary: all four quarters with personal completed/active/
    //                     upcoming/locked status for the quarter selector
    //   - currentQuarter: the quarter key (Q1/Q2/Q3/Q4)
    //   - nextQuarter:    the next quarter key or null if on Q4
    getMyQuarter: builder.query<MyQuarterResponse, void>({
      query:        () => "/programme/my-quarter",
      providesTags: [{ type: "ProgrammeCycle", id: "MY_QUARTER" }],
    }),

    // GET /api/programme/active — global org-wide active quarter (admin use).
    // Mentees should use getMyQuarter instead.
    getActiveCycle: builder.query<CycleResponse, void>({
      query:        () => "/programme/active",
      providesTags: [{ type: "ProgrammeCycle", id: "ACTIVE" }],
    }),

    // GET /api/programme/cycles/:quarter
    getCycleByQuarter: builder.query<CycleResponse, { quarter: QuarterKey; year?: number }>({
      query: ({ quarter, year }) => ({
        url:    `/programme/cycles/${quarter}`,
        params: year ? { year } : undefined,
      }),
      providesTags: (_result, _error, { quarter }) => [
        { type: "ProgrammeCycle", id: quarter },
      ],
    }),

    // POST /api/programme/my-quarter/advance — move the mentee to their next quarter.
    // Admin can pass { menteeId } to advance a specific mentee.
    // Mentees advancing themselves must have all tasks graded first.
    advanceMyQuarter: builder.mutation<
      AdvanceQuarterResponse,
      { menteeId?: string } | void
    >({
      query: (body) => ({
        url:    "/programme/my-quarter/advance",
        method: "POST",
        body:   body || {},
      }),
      invalidatesTags: [
        { type: "ProgrammeCycle", id: "MY_QUARTER" },
        { type: "JourneyProfile",  id: "CURRENT"   },
        { type: "JourneyQuarters", id: "ALL"        },
        { type: "Task",            id: "LIST"       },
      ],
    }),

    // POST /api/programme/cycles — admin: create a cycle
    createCycle: builder.mutation<CycleResponse, CreateCycleRequest>({
      query: (body) => ({ url: "/programme/cycles", method: "POST", body }),
      invalidatesTags: [{ type: "ProgrammeCycle", id: "LIST" }],
    }),

    // PATCH /api/programme/cycles/:id — admin: update a cycle
    updateCycle: builder.mutation<
      CycleResponse,
      { id: string } & Partial<CreateCycleRequest>
    >({
      query: ({ id, ...body }) => ({
        url:    `/programme/cycles/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "ProgrammeCycle", id: "LIST"       },
        { type: "ProgrammeCycle", id: "ACTIVE"     },
        { type: "ProgrammeCycle", id: "MY_QUARTER" },
      ],
    }),
  }),
});

export const {
  useGetAllCyclesQuery,
  useGetMyQuarterQuery,          // ← primary hook for all mentee-facing tabs
  useGetActiveCycleQuery,        // ← admin/org-level use only
  useGetCycleByQuarterQuery,
  useAdvanceMyQuarterMutation,
  useCreateCycleMutation,
  useUpdateCycleMutation,
} = programmeApi;